/**
 * NOS-223: Display-only inline preview of the imported homepage, shown while
 * an import job is paused at the NOS-192 preview gate.
 *
 * Renders an iframe pointing at the public site route (/{orgSlug}) so the user
 * sees the real imported navigation, sections, and footer with pixel parity —
 * without leaving onboarding. The clone path seeds the home page as published
 * (src/themes/default.json) and map-site-content only replaces its sections,
 * so the homepage is renderable at the public route during review.
 *
 * The page is rendered at a desktop virtual width and scaled down with
 * transform: scale(). The preview is scrollable but interaction-inert: the
 * iframe is same-origin, so on load we inject CSS + a capture-phase click
 * blocker that neuter links/buttons/inputs while native scrolling still works.
 * If the frame document is ever inaccessible, we fall back to a transparent
 * shield (display-only, no scroll) rather than risk a click-through.
 *
 * An optional expand control opens the same preview larger in a modal — the
 * user never leaves the onboarding flow.
 */
import { useCallback, useLayoutEffect, useRef, useState } from "react"
import { Loader2, Maximize2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

/** Desktop viewport width the preview renders at before being scaled down. */
export const PREVIEW_VIRTUAL_WIDTH = 1280
/** 4:3 pane — matches the default container's aspect ratio below. */
export const PREVIEW_VIRTUAL_HEIGHT = 960

export interface ImportPreviewPaneProps {
  /** Slug of the org whose imported homepage should be previewed. */
  orgSlug: string | null | undefined
  className?: string
  /**
   * When true, the pane fills its parent's height instead of forcing a 4:3
   * aspect (used inside the expand modal).
   */
  fill?: boolean
  /** Show the expand-in-modal control (default false). */
  expandable?: boolean
}

/**
 * Make the (same-origin) framed document scroll-only: links, buttons, and form
 * controls stop responding, and any click that slips through is swallowed in
 * the capture phase. Returns false when the document can't be reached.
 */
function makeFrameInert(frame: HTMLIFrameElement): boolean {
  try {
    const doc = frame.contentDocument
    if (!doc) return false
    if (!doc.getElementById("nos-preview-inert")) {
      const style = doc.createElement("style")
      style.id = "nos-preview-inert"
      style.textContent =
        "a, button, input, textarea, select, label, [role=button] { pointer-events: none !important; cursor: default !important; }"
      ;(doc.head ?? doc.documentElement).appendChild(style)
    }
    const swallow = (e: Event) => {
      e.preventDefault()
      e.stopPropagation()
    }
    doc.addEventListener("click", swallow, true)
    doc.addEventListener("submit", swallow, true)
    return true
  } catch {
    return false
  }
}

export function ImportPreviewPane({
  orgSlug,
  className,
  fill = false,
  expandable = false,
}: ImportPreviewPaneProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  // Default to 1 so environments without layout (tests) still render the iframe.
  const [scale, setScale] = useState(1)
  const [virtualHeight, setVirtualHeight] = useState(PREVIEW_VIRTUAL_HEIGHT)
  const [loaded, setLoaded] = useState(false)
  // True when the frame document couldn't be neutered — shield it instead.
  const [shielded, setShielded] = useState(false)
  const [expanded, setExpanded] = useState(false)

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => {
      const width = el.clientWidth
      if (width <= 0) return
      const nextScale = width / PREVIEW_VIRTUAL_WIDTH
      setScale(nextScale)
      // Fill panes derive the virtual height from the real one so the framed
      // viewport covers the whole container; 4:3 panes keep the fixed height.
      const height = el.clientHeight
      if (fill && height > 0) setVirtualHeight(height / nextScale)
    }
    measure()
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [fill])

  const handleLoad = useCallback((e: React.SyntheticEvent<HTMLIFrameElement>) => {
    setLoaded(true)
    setShielded(!makeFrameInert(e.currentTarget))
  }, [])

  if (!orgSlug) {
    return (
      <div
        data-testid="import-preview-pane-fallback"
        className={`flex aspect-[4/3] w-full items-center justify-center rounded-lg border bg-muted/30 p-6 ${className ?? ""}`}
      >
        <p className="max-w-xs text-center text-sm text-muted-foreground">
          Your live preview isn&apos;t available yet. Your imported sample is safe —
          you can still decide below.
        </p>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      data-testid="import-preview-pane"
      className={`relative w-full overflow-hidden rounded-lg border bg-background shadow-sm ${
        fill ? "h-full" : "aspect-[4/3]"
      } ${className ?? ""}`}
    >
      {!loaded && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted/40">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="sr-only">Loading preview…</span>
        </div>
      )}
      <iframe
        src={`/${orgSlug}`}
        title="Preview of your imported site"
        tabIndex={-1}
        onLoad={handleLoad}
        className="origin-top-left select-none border-0"
        style={{
          width: PREVIEW_VIRTUAL_WIDTH,
          height: virtualHeight,
          transform: `scale(${scale})`,
        }}
      />
      {/* Inert shield — only when the frame document couldn't be neutered. */}
      {shielded && (
        <div className="absolute inset-0 z-20" aria-hidden="true" data-testid="import-preview-shield" />
      )}
      {expandable && (
        <>
          <button
            type="button"
            aria-label="Expand preview"
            onClick={() => setExpanded(true)}
            className="absolute right-2 top-2 z-30 rounded-md border bg-background/90 p-1.5 text-muted-foreground shadow-sm transition-colors hover:bg-background hover:text-foreground"
          >
            <Maximize2 className="h-4 w-4" aria-hidden="true" />
          </button>
          <Dialog open={expanded} onOpenChange={setExpanded}>
            <DialogContent className="flex h-[85vh] w-[92vw] max-w-6xl flex-col gap-3 p-4">
              <DialogHeader className="space-y-0">
                <DialogTitle className="text-base">Preview of your imported site</DialogTitle>
              </DialogHeader>
              <div className="min-h-0 flex-1">
                {expanded && <ImportPreviewPane orgSlug={orgSlug} fill />}
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  )
}
